import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { postOverlay, num, str } from '@/lib/overlayApi';
import type { DateRange } from '@/lib/dateRange';

export interface StoreRow {
  store_id: string;
  label: string;
  osa_pct: number | null;
  ls_pct: number | null;
  osa_in_ls_pct: number | null;
  psl: number | null;
  doi: number | null;
  soh: number | null;
  drr: number | null;
}

/** Stores shown at once. The drill-down is a triage list, not an export. */
const MAX_STORES = 200;

/**
 * Dark-store drill-down behind a SKU row.
 *
 * One request. The server joins store_metrics to store_daily inside a single
 * statement, which is what fixed the class of bug this hook used to have: two
 * client-side reads sorted differently, returned two different 200-store
 * slices, and every LS% column rendered "—" because the join missed.
 *
 * LS% and OSA-in-LS% come from COUNTS, never from averaging a ratio:
 *    LS%        = days listed ÷ days observed
 *    OSA in LS% = days in stock ÷ days listed
 */
export function useStoreDrilldown(
  customerName: string,
  productId: string | undefined,
  city: string | undefined,
  range: DateRange,
  enabled: boolean,
): UseQueryResult<StoreRow[], Error> {
  return useQuery({
    queryKey: ['store-drilldown', customerName, productId, city, range],
    enabled: enabled && Boolean(productId),
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => {
      // `enabled` already gates on productId; this keeps the type honest rather
      // than asserting it away, so a future caller cannot slip past the gate.
      if (!productId) return [];
      const body: Record<string, unknown> = {
        customer_name: customerName,
        product_id: productId,
        window: range,
        limit: MAX_STORES,
      };
      if (city) body['city'] = city;

      const res = await postOverlay<Record<string, unknown>>('/v1/availability/stores', body, signal);
      return res.rows.map((r): StoreRow => {
        const id = str(r['store_id']);
        const locality = str(r['locality']);
        return {
          store_id: id,
          label: locality ? `${locality}-${id}` : id,
          osa_pct: num(r['osa_pct']),
          ls_pct: num(r['ls_pct']),
          osa_in_ls_pct: num(r['osa_in_ls_pct']),
          psl: num(r['psl']),
          doi: num(r['doi']),
          soh: num(r['soh']),
          drr: num(r['drr']),
        };
      });
    },
  });
}

/** Bands the store's PSL against the busiest store in the same list. */
export function pslBand(psl: number | null, max: number): 'High' | 'Medium' | 'Low' | null {
  if (psl == null || max <= 0) return null;
  const share = psl / max;
  if (share >= 0.5) return 'High';
  if (share >= 0.2) return 'Medium';
  return 'Low';
}
